'use client'

import { useTranslations } from 'next-intl'
import { useCallback, useMemo, useRef, useState } from 'react'
import { EmptyState } from '@/components/ui/empty-state'
import { useInfiniteScrollSentinel } from '@/hooks/use-infinite-scroll-sentinel'
import { matchFlowCardsByBrand } from '@/lib/flow-templates/brand-search'
import { useFlowSearchQuery } from '@/lib/flow-templates/search-query-state'
import type { PublicFlowTemplateCard } from '@/lib/flow-templates/types'
import { FLOW_GRID_CLASSES } from '../lib/grid-classes'
import { buildFlowTemplatePath } from '../lib/url-helpers'
import { FlowCard } from './flow-card'
import { TemplateGridReveal } from './template-grid-reveal'

/**
 * Flow cards revealed per page. A flow card is a three-up strip of 9:16
 * emails, so one page is six rows even at the widest column count — taller
 * than any viewport, which is what lets the sentinel clear the prefetch
 * margin after a page and wait for the next scroll instead of unrolling
 * the whole gallery in one go.
 */
const FLOWS_PAGE_SIZE = 24

/**
 * How many of the matched flows are mounted. The count is stamped with the
 * query it was reached under, so ANY move of the query — typed, cleared, or
 * back to one searched earlier — is one page again, and the depth reached
 * under the old ranking never leaks into the new one.
 */
function useFlowWindow(query: string, matchCount: number) {
  const [reached, setReached] = useState({ query, count: FLOWS_PAGE_SIZE })
  if (reached.query !== query) {
    // React's sanctioned adjust-state-during-render: it re-renders before
    // committing, so the new query is never painted at the old depth and
    // this costs no effect and no extra frame.
    setReached({ query, count: FLOWS_PAGE_SIZE })
  }
  const visibleCount = Math.min(
    reached.query === query ? reached.count : FLOWS_PAGE_SIZE,
    matchCount
  )
  const hasMore = visibleCount < matchCount
  // Closing over `visibleCount` is load-bearing, not incidental: the new
  // identity is what re-arms the sentinel's observer for the page after
  // this one (see `useInfiniteScrollSentinel`).
  const showMore = useCallback(() => {
    setReached({ query, count: visibleCount + FLOWS_PAGE_SIZE })
  }, [query, visibleCount])
  return { visibleCount, hasMore, showMore }
}

/**
 * The Flows collection with its search: every flow until the visitor
 * types, then the brands that match, best first, filtered on the client
 * the moment a key lands — the shared `q` state feeds this and the search
 * bar alike, and reaches the URL after a debounce. No index round-trip:
 * the page holds every public flow.
 *
 * Held, not mounted: the gallery renders a page at a time and grows as the
 * bottom sentinel nears the scroll boundary, so a search still ranks every
 * flow the server sent while first paint only pays for the cards on screen.
 */
export function FlowsGridClient({
  initialCards,
  basePath,
}: {
  initialCards: ReadonlyArray<PublicFlowTemplateCard>
  /**
   * `/flows`, or `/browse/flows` in the reader's language: the gallery
   * itself and the root of every card link.
   */
  basePath: string
}) {
  const t = useTranslations('flows')
  const [query, setQuery] = useFlowSearchQuery()
  const cards = useMemo(
    () => matchFlowCardsByBrand(initialCards, query),
    [initialCards, query]
  )
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { visibleCount, hasMore, showMore } = useFlowWindow(query, cards.length)
  useInfiniteScrollSentinel(sentinelRef, hasMore, showMore)

  const handleClearSearch = useCallback(() => {
    void setQuery('')
  }, [setQuery])

  if (query.trim().length > 0 && cards.length === 0) {
    return (
      <EmptyState
        action={{ label: t('search.clearSearch'), onClick: handleClearSearch }}
        reason="no-results"
        title={t('gallery.noMatches', { query: query.trim() })}
      />
    )
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className={FLOW_GRID_CLASSES}>
        {cards.slice(0, visibleCount).map((card, index) => (
          <TemplateGridReveal key={card.slug} inView delay={(index % 6) * 0.06}>
            <FlowCard
              card={card}
              href={buildFlowTemplatePath(card.slug, basePath)}
            />
          </TemplateGridReveal>
        ))}
      </div>
      {hasMore ? <div ref={sentinelRef} className="h-px w-full" /> : null}
    </div>
  )
}
